import type { Pos } from '@games/core'
import { COLS, ROWS } from './types'
import type { Board, Color, Piece, PieceType } from './types'

/** 一个目标格；`visionOnly: true` 表示只提供视野、不能落子（炮架）。 */
export interface LegalTarget {
  readonly r: number
  readonly c: number
  readonly visionOnly?: boolean
}

const BACK_ROW: readonly PieceType[] = ['R', 'N', 'B', 'A', 'K', 'A', 'B', 'N', 'R']

const ORTHOGONAL: readonly (readonly [number, number])[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
]

const DIAGONAL: readonly (readonly [number, number])[] = [
  [-1, -1],
  [-1, 1],
  [1, -1],
  [1, 1],
]

const ELEPHANT_STEPS: readonly (readonly [number, number])[] = [
  [-2, -2],
  [-2, 2],
  [2, -2],
  [2, 2],
]

const HORSE_LEGS: readonly { readonly d: readonly [number, number]; readonly leg: readonly [number, number] }[] = [
  { d: [-2, -1], leg: [-1, 0] },
  { d: [-2, 1], leg: [-1, 0] },
  { d: [2, -1], leg: [1, 0] },
  { d: [2, 1], leg: [1, 0] },
  { d: [-1, -2], leg: [0, -1] },
  { d: [1, -2], leg: [0, -1] },
  { d: [-1, 2], leg: [0, 1] },
  { d: [1, 2], leg: [0, 1] },
]

/** 新建一个 10×9 的空棋盘。 */
export function emptyBoard(): Board {
  return Array.from({ length: ROWS }, () => Array.from({ length: COLS }, () => null as Piece | null))
}

/** 旧版 `createInitialBoard`：黑方在上（第 0 行），红方在下（第 9 行），红先行。 */
export function createInitialBoard(): Board {
  const board = Array.from({ length: ROWS }, () => Array.from({ length: COLS }, () => null as Piece | null))
  for (let c = 0; c < COLS; c++) {
    board[0][c] = { color: 'b', type: BACK_ROW[c] }
    board[9][c] = { color: 'r', type: BACK_ROW[c] }
  }
  board[2][1] = { color: 'b', type: 'C' }
  board[2][7] = { color: 'b', type: 'C' }
  board[7][1] = { color: 'r', type: 'C' }
  board[7][7] = { color: 'r', type: 'C' }
  for (let c = 0; c < COLS; c += 2) {
    board[3][c] = { color: 'b', type: 'P' }
    board[6][c] = { color: 'r', type: 'P' }
  }
  return board
}

/** 九宫格判定。 */
export function inPalace(r: number, c: number, color: Color): boolean {
  if (c < 3 || c > 5) return false
  return color === 'b' ? r >= 0 && r <= 2 : r >= 7 && r <= 9
}

/**
 * 生成某个棋子的伪合法走法（该变体没有「将军」限制，直接吃掉将/帅即获胜）。
 * 逐条对应旧版 `web/index.html` 第 529–629 行的 `genMoves`。
 */
export function genMoves(board: Board, r: number, c: number): ReadonlyArray<LegalTarget> {
  const piece = board[r][c]
  if (!piece) return []
  const { color, type } = piece
  const moves: LegalTarget[] = []

  const addIfOk = (nr: number, nc: number): void => {
    if (nr < 0 || nr > 9 || nc < 0 || nc > 8) return
    const target = board[nr][nc]
    if (!target || target.color !== color) moves.push({ r: nr, c: nc })
  }

  switch (type) {
    case 'K': {
      for (const [dr, dc] of ORTHOGONAL) {
        const nr = r + dr
        const nc = c + dc
        if (inPalace(nr, nc, color)) addIfOk(nr, nc)
      }
      // 将帅照面：沿本列扫描，遇到的第一个棋子若是对方将/帅则可直接吃掉
      for (const dir of [-1, 1]) {
        let nr = r + dir
        while (nr >= 0 && nr <= 9) {
          const target = board[nr][c]
          if (target) {
            if (target.type === 'K' && target.color !== color) moves.push({ r: nr, c })
            break
          }
          nr += dir
        }
      }
      break
    }
    case 'A': {
      for (const [dr, dc] of DIAGONAL) {
        const nr = r + dr
        const nc = c + dc
        if (inPalace(nr, nc, color)) addIfOk(nr, nc)
      }
      break
    }
    case 'B': {
      for (const [dr, dc] of ELEPHANT_STEPS) {
        const nr = r + dr
        const nc = c + dc
        if (nr < 0 || nr > 9 || nc < 0 || nc > 8) continue
        if (color === 'b' && nr > 4) continue
        if (color === 'r' && nr < 5) continue
        if (board[r + dr / 2][c + dc / 2]) continue
        addIfOk(nr, nc)
      }
      break
    }
    case 'N': {
      for (const { d, leg } of HORSE_LEGS) {
        const nr = r + d[0]
        const nc = c + d[1]
        if (nr < 0 || nr > 9 || nc < 0 || nc > 8) continue
        if (board[r + leg[0]][c + leg[1]]) continue
        addIfOk(nr, nc)
      }
      break
    }
    case 'R': {
      for (const [dr, dc] of ORTHOGONAL) {
        let nr = r + dr
        let nc = c + dc
        while (nr >= 0 && nr <= 9 && nc >= 0 && nc <= 8) {
          const target = board[nr][nc]
          if (!target) {
            moves.push({ r: nr, c: nc })
          } else {
            if (target.color !== color) moves.push({ r: nr, c: nc })
            break
          }
          nr += dr
          nc += dc
        }
      }
      break
    }
    case 'C': {
      for (const [dr, dc] of ORTHOGONAL) {
        let nr = r + dr
        let nc = c + dc
        let screenPassed = false
        while (nr >= 0 && nr <= 9 && nc >= 0 && nc <= 8) {
          const target = board[nr][nc]
          if (!screenPassed) {
            if (!target) moves.push({ r: nr, c: nc })
            // 炮架本身不是合法落点，但视野应该能看到是谁挡住了炮
            else {
              moves.push({ r: nr, c: nc, visionOnly: true })
              screenPassed = true
            }
          } else if (target) {
            if (target.color !== color) moves.push({ r: nr, c: nc })
            break
          }
          nr += dr
          nc += dc
        }
      }
      break
    }
    case 'P': {
      const forward = color === 'r' ? -1 : 1
      const crossed = color === 'r' ? r <= 4 : r >= 5
      const candidates: (readonly [number, number])[] = [[forward, 0]]
      if (crossed) candidates.push([0, -1], [0, 1])
      for (const [dr, dc] of candidates) addIfOk(r + dr, c + dc)
      break
    }
  }

  return moves
}

/** 纯函数：返回 `{ board, captured }`，不修改入参。 */
export function applyMove(board: Board, from: Pos, to: Pos): { board: Board; captured: Piece | null } {
  const captured = board[to.r][to.c]
  const moving = board[from.r][from.c]
  const next = board.map((row, r) =>
    row.map((piece, c) => {
      if (r === from.r && c === from.c) return null
      if (r === to.r && c === to.c) return moving
      return piece
    }),
  )
  return { board: next, captured }
}